import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import { Button, Col, Row } from 'react-bootstrap';
import Autocomplete from 'react-google-autocomplete';
import L from "leaflet";
import { Circle, MapContainer as LeafletMap, Marker, Tooltip as MapTooltip, ScaleControl, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AuthContext } from "../accounts/AccountsReducer";
import MarkerClusterGroup from 'react-leaflet-cluster';
import {
  faCircle, faTimesCircle, faExclamationCircle, faHome, faMapMarkerAlt, faStar, faReply,
} from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle, faQuestionCircle as faQuestionCircleDuo } from '@fortawesome/pro-duotone-svg-icons';
import { faHomeAlt as faHomeAltReg } from '@fortawesome/pro-regular-svg-icons';
import { faCircleBolt, faHomeAlt, faLocationCrosshairs, faDoNotEnter, faHouseMedical, faLocationCheck, faLocationExclamation, faHouseMedicalCircleCheck, faHouseMedicalCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import FirePerimetersLayer from './FirePerimetersLayer';
import EvacZonesLayer from './EvacZonesLayer';
import CaltopoFeaturesLayer from './CaltopoFeaturesLayer';


function MapEventsHandler({ onMoveEnd }) {
  useMapEvents({
    dragend: (e) => {
      if (onMoveEnd) {
        onMoveEnd(e);
      }
    },
    zoomend: (e) => {
      if (onMoveEnd) {
        onMoveEnd(e);
      }
    },
  });
  return null; // This component doesn't render any UI
}

export const MapResizer = (props) => {
  const map = useMap(); // Get the Leaflet map instance

  // Example: Invalidate size after a delay (e.g., when a modal opens)
  useEffect(() => {
    if (props.bounds.length && props.bounds[0][0]) {
      const timeoutId = setTimeout(() => {
        map.invalidateSize();
        map.fitBounds(props.bounds, {maxZoom:15})
      }, 10); // Adjust delay as needed
      return () => clearTimeout(timeoutId);
    }
  }, [props.showFilters]);

  return null; // This component doesn't render anything visually
};

// A custom component to handle map events and update zoom state
export const ZoomHandler = ({ setZoomLevel }) => {

  useMapEvents({
    zoomend: (e) => {
      setZoomLevel(e.target.getZoom());
    },
  });
  return null;
}

export function BoundsUpdater({ bounds }) {
  const map = useMap();

  useEffect(() => {
    if (map && bounds && bounds.length > 1) {
      map.fitBounds(bounds);
    }
    else if (map && bounds && bounds.length === 1) {
      map.panTo(bounds[0], map.getZoom())
    }
  }, [map, bounds]); // Re-run effect when map or bounds change

  return null; // This component doesn't render anything visually
}

export const LocationMarker = React.forwardRef((props, ref) => {

  const [latLng, setLatLng] = useState({lat:props.values.latitude, lng:props.values.longitude});

  const pinIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon color="red" size="lg" className="icon-border" icon={faMapMarkerAlt} />);
  const pinMarkerIcon = new L.DivIcon({
    html: pinIconHTML,
    iconSize: [0, 0],
    iconAnchor: [5, 10],
    className: "pin-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });

  const map = useMapEvents({
    click(e) {
      map.locate();
      setLatLng(e.latlng)
      if (props.setOptions) {
        props.setOptions({...props.options, latlng: e.latlng});
      }
      if (props.setFieldValue) {
        if (!props.id) {
          props.setFieldValue("latlon", e.latlng.lat.toFixed(4) + ', ' + e.latlng.lng.toFixed(4))
          props.setFieldValue("latitude", +(Math.round(e.latlng.lat + "e+4") + "e-4"));
          props.setFieldValue("longitude", +(Math.round(e.latlng.lng + "e+4") + "e-4"));
        }
      }
    },
  })

  useEffect(() => {
    setLatLng({lat:props.values.latitude, lng:props.values.longitude})
  }, [props.values.latitude, props.values.longitude]);

  return latLng === null ? null : (
  <>
    <Marker
      draggable={props.draggable || false}
      eventHandlers={{
        dragend() {
          if (props.draggable) {
            props.updatePosition(props.setFieldValue);
          }
        },
      }}
      position={latLng}
      icon={pinMarkerIcon}
      ref={ref}
    >
      <MapTooltip autoPan={false} direction="top">
        <div>
          Lat: {+(Math.round(latLng.lat + "e+4") + "e-4")}, Lon: {+(Math.round(latLng.lng + "e+4") + "e-4")}
        </div>
      </MapTooltip>
    </Marker>
    {props.options ? <Circle center={latLng} color={'#ff4c4c'} radius={(props.options.radius * 1609.34)/2} interactive={false} /> : ""}
  </>
  )
})

export const CircleMarker = React.forwardRef((props, ref) => {

  const [latLng, setLatLng] = useState({lat:props.center.lat, lng:props.center.lng});

  const map = ref.current
  const circleIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon color={props.fillColor} size="lg" className="icon-border" icon={faCircle} style={{strokeWidth:props.weight}} transform={'grow-' + (map && map.getZoom() >= 17 ? '10' : map && map.getZoom() >= 16 ? '4' : '0')} />);
  const circleMarkerIcon = new L.DivIcon({
    html: circleIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 11],
    className: "circle-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });

  useEffect(() => {
    setLatLng({lat:props.center.lat, lng:props.center.lng})
  }, [props.center.lat, props.center.lng]);

  return useMemo(() => (
    <Marker
      draggable={false}
      position={latLng}
      icon={circleMarkerIcon}
      // ref={ref}
      eventHandlers={props.eventHandlers}
      props={{...props}}
    >
      {props.children}
    </Marker>
  ))
})

export const createPinMarkerIcon = (size) => {

  const pinIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon color="red" size="lg" className="icon-border" icon={faMapMarkerAlt} transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />);

  return L.divIcon({
    html: pinIconHTML,
    iconSize: [0, 0],
    iconAnchor: [6, 20],
    className: "pin-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createStarMarkerIcon = (size) => {

  const starIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon color="gold" size="lg" className="icon-border" icon={faStar} transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />);

  return L.divIcon({
    html: starIconHTML,
    iconSize: [0, 0],
    iconAnchor: [9, 10],
    className: "star-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createReportedMarkerIcon = (size) => {

  const reportedIconHTML = ReactDOMServer.renderToString(
    <span className="fa-layers">
      <FontAwesomeIcon icon={faCircle} size="lg" color="white" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
      <FontAwesomeIcon icon={faExclamationCircle} size="lg" className="icon-border" color="#ff4c4c" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
    </span>
  );

  return L.divIcon({
    html: reportedIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "reported-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createReportedEvacMarkerIcon = (size) => {

  const reportedEvacIconHTML = ReactDOMServer.renderToString(
    <span className="fa-layers">
      <FontAwesomeIcon icon={faCircle} color="white" size="lg" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
      <FontAwesomeIcon icon={faCircleBolt} className="icon-border" size="lg" color="#ff4c4c" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
    </span>
  );

  return L.divIcon({
    html: reportedEvacIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "reported-evac-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createReportedSIPMarkerIcon = (size) => {

  const reportedSIPIconHTML = ReactDOMServer.renderToString(
    <span className="fa-layers">
      <FontAwesomeIcon icon={faCircle} className="icon-border" color="#ff4c4c" size="lg" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
      <FontAwesomeIcon icon={faHomeAlt} style={{color:"white"}} transform={'shrink-6 ' + 'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} size="lg" inverse />
      <FontAwesomeIcon icon={faHomeAltReg} style={{color:"#444"}} transform={'shrink-6 ' + 'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} size="lg" inverse />
    </span>
  );

  return L.divIcon({
    html: reportedSIPIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "reported-sip-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createSIPMarkerIcon = (size) => {

  const SIPIconHTML = ReactDOMServer.renderToString(
    <span className="fa-layers">
      <FontAwesomeIcon icon={faCircle} className="icon-border" color="#f5ee0f" size="lg" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
      <FontAwesomeIcon icon={faHomeAlt} style={{color:"white"}} transform={'shrink-6 ' + 'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} size="lg" inverse />
      <FontAwesomeIcon icon={faHomeAltReg} style={{color:"#444"}} transform={'shrink-6 ' + 'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} size="lg" inverse />
    </span>
  );

  return L.divIcon({
    html: SIPIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "SIP-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createUTLMarkerIcon = (size) => {

  const UTLIconHTML = ReactDOMServer.renderToString(
    <span className="fa-layers">
      <FontAwesomeIcon icon={faCircle} color="white" size="lg" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
      <FontAwesomeIcon icon={faQuestionCircleDuo} className="icon-border" size="lg" style={{"--fa-primary-color":'white', "--fa-secondary-color":'#5f5fff', "--fa-secondary-opacity": 1}} transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
    </span>
  );

  return L.divIcon({
    html: UTLIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "UTL-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createCheckMarkerIcon = (size) => {

  const CheckIconHTML = ReactDOMServer.renderToString(
    <FontAwesomeIcon icon={faCheckCircle} className="icon-border" size="lg" style={{"--fa-primary-color":'white', "--fa-secondary-color":'green', "--fa-secondary-opacity": 1}} transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
  );

  return L.divIcon({
    html: CheckIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "check-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

const shelterIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faHome} className="icon-border" size="lg" color="#b18662" />);
export const shelterMarkerIcon = new L.DivIcon({
  html: shelterIconHTML,
  iconSize: [0, 0],
  iconAnchor: [8, 9],
  className: "shelter-icon",
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

const vetShelterIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faHouseMedical} className="icon-border" size="lg" color="#b18662" />);
export const vetShelterMarkerIcon = new L.DivIcon({
  html: vetShelterIconHTML,
  iconSize: [0, 0],
  iconAnchor: [8, 9],
  className: "vet-shelter-icon",
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

const vetCompleteShelterIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faHouseMedicalCircleCheck} className="icon-border" size="lg" color="#b18662" />);
export const vetCompleteShelterMarkerIcon = new L.DivIcon({
  html: vetCompleteShelterIconHTML,
  iconSize: [0, 0],
  iconAnchor: [8, 9],
  className: "vet-complete-shelter-icon",
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

const vetPendingShelterIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faHouseMedicalCircleExclamation} className="icon-border" size="lg" color="#b18662" />);
export const vetPendingShelterMarkerIcon = new L.DivIcon({
  html: vetPendingShelterIconHTML,
  iconSize: [0, 0],
  iconAnchor: [8, 9],
  className: "vet-pending-shelter-icon",
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

const vetPendingAnimalLocationIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faLocationExclamation} className="icon-border" size="lg" color="#b18662" />);
export const vetPendingAnimalLocationMarkerIcon = new L.DivIcon({
  html: vetPendingAnimalLocationIconHTML,
  iconSize: [0, 0],
  iconAnchor: [8, 9],
  className: "vet-pending-animal-icon",
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

const vetCompleteAnimalLocationIconHTML = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faLocationCheck} className="icon-border" size="lg" color="#b18662" />);
export const vetCompleteAnimalLocationMarkerIcon = new L.DivIcon({
  html: vetCompleteAnimalLocationIconHTML,
  iconSize: [0, 0],
  iconAnchor: [8, 9],
  className: "vet-complete-animal-icon",
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

export const createClosedMarkerIcon = (size) => {

  const ClosedIconHTML = ReactDOMServer.renderToString(
    <span className="fa-layers">
      <FontAwesomeIcon icon={faCircle} color="white" size="lg" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
      <FontAwesomeIcon icon={faDoNotEnter} className="icon-border" size="lg" color="#353737" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
    </span>
  );

  return L.divIcon({
    html: ClosedIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "closed-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

const finishedIconHTML = ReactDOMServer.renderToString(
  <span className="fa-layers">
    <FontAwesomeIcon icon={faCircle} color="white" size="lg" />
    <FontAwesomeIcon icon={faTimesCircle} className="icon-border" size="lg" color="#af7051" />
  </span>
);
export const finishedMarkerIcon = new L.DivIcon({
  html: finishedIconHTML,
  iconSize: [0, 0],
  iconAnchor: [8, 9],
  className: "finished-icon",
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

export const createOperationsMarkerIcon = (size) => {

  const OperationsIconHTML = ReactDOMServer.renderToString(
    <span className="fa-layers">
      <FontAwesomeIcon icon={faCircle} className="icon-border" color="grey" size="lg" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
      <FontAwesomeIcon icon={faLocationCrosshairs} className="icon-border" size="lg" color="white" transform={'left-1 shrink-3 grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
    </span>
  );

  return L.divIcon({
    html: OperationsIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "operations-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createReturnRequestedMarkerIcon = (size) => {

  const ReturnRequestedIconHTML = ReactDOMServer.renderToString(
    <span className="fa-layers">
      <FontAwesomeIcon icon={faCircle} className="icon-border" color="#af7051" size="lg" transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
      <FontAwesomeIcon icon={faReply} className="icon-border" size="lg" color="white" transform={'shrink-6 grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
    </span>
  );

  return L.divIcon({
    html: ReturnRequestedIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "transport-requested-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

export const createGrayCheckMarkerIcon = (size) => {

  const GrayCheckIconHTML = ReactDOMServer.renderToString(
    <FontAwesomeIcon icon={faCheckCircle} className="icon-border" size="lg" style={{"--fa-primary-color":'white', "--fa-secondary-color":'gray', "--fa-secondary-opacity": 1}} transform={'grow-' + (size >= 17 ? '10' : size >= 16 ? '4' : '0')} />
  );

  return L.divIcon({
    html: GrayCheckIconHTML,
    iconSize: [0, 0],
    iconAnchor: [8, 9],
    className: "gray-check-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  });
};

// Counts the number of size/species matches for a service request by status.
export const countMatches = (animals, species) => {

  var matches = {};
  var status_matches = {'REPORTED':{}, 'REPORTED (EVAC REQUESTED)':{}, 'REPORTED (SIP REQUESTED)':{}, 'SHELTERED IN PLACE':{}, 'UNABLE TO LOCATE':{}, 'TRANSPORT REQUESTED':{}};

  animals.forEach((animal) => {
    // Count singular and plural species as the same.
    if (species.filter(specie => specie.plural_name === animal.species_string).length > 0) {
      animal.species_string = species.filter(specie => specie.plural_name === animal.species_string)[0].name
    }
    if (['REPORTED', 'REPORTED (EVAC REQUESTED)', 'REPORTED (SIP REQUESTED)', 'SHELTERED IN PLACE', 'UNABLE TO LOCATE', 'TRANSPORT REQUESTED'].indexOf(animal.status) > -1) {
      if (!matches[[animal.species_string]]) {
        matches[[animal.species_string]] = Number(animal.animal_count);
      }
      else {
        matches[[animal.species_string]] += Number(animal.animal_count);
      }
      if (!status_matches[animal.status][[animal.species_string]]) {
        status_matches[animal.status][[animal.species_string]] = Number(animal.animal_count);
      }
      else {
        status_matches[animal.status][[animal.species_string]] += Number(animal.animal_count);
      }
    }
  });
  return [matches, status_matches]
}

// Counts the number of species matches for an assigned request.
export const countDictMatches = (animals, species, status=false) => {
  var matches = {};
  var status_matches = {'REPORTED':{}, 'REPORTED (EVAC REQUESTED)':{}, 'REPORTED (SIP REQUESTED)':{}, 'SHELTERED IN PLACE':{}, 'UNABLE TO LOCATE':{}, 'SHELTERED':{}, 'DECEASED':{}, 'REUNITED':{}, 'CARE TRANSFERRED':{}, 'TRANSPORT REQUESTED':{}};

  Object.keys(animals).forEach((animal) => {

    // Count singular and plural species as the same.
    if (species.filter(specie => specie.plural_name === animals[animal].species).length > 0) {
      animals[animal].species = species.filter(specie => specie.plural_name === animals[animal].species)[0].name
    }

    if (['REPORTED', 'REPORTED (EVAC REQUESTED)', 'REPORTED (SIP REQUESTED)', 'SHELTERED IN PLACE', 'UNABLE TO LOCATE', 'SHELTERED', 'DECEASED', 'REUNITED', 'CARE TRANSFERRED', 'TRANSPORT REQUESTED'].indexOf(animals[animal].status) > -1) {
      if (!matches[[animals[animal].species]]) {
        matches[[animals[animal].species]] = Number(animals[animal].animal_count) || 1;
      }
      else {
        matches[[animals[animal].species]] += Number(animals[animal].animal_count) || 1;
      }
      if (status) {
        if (!status_matches[animals[animal].status][[animals[animal].species]]) {
          status_matches[animals[animal].status][[animals[animal].species]] = Number(animals[animal].animal_count) || 1;
        }
        else {
          status_matches[animals[animal].status][[animals[animal].species]] += Number(animals[animal].animal_count) || 1;
        }
      }
    }
  });
  if (status) {
    return [matches, status_matches]
  }
  return matches
}

// Takes in animal size, species, and count and returns a pretty string combination.
export const prettyText = (species, count, species_data) => {

  if (count <= 0) {
    return "";
  }
  else if (species === "animalless") {
    if (count > 1) {
      species = "locations";
    }
    else {
      species = "location";
    }
  }
  else if (count > 1) {
    species = species_data.filter(specie => (specie.name === species || specie.plural_name === species))[0].plural_name;
  }

  let text = count + " " + species;

  return text;
}

// const Map = (props) => {
const Map = React.forwardRef((props, ref) => {
  // const markerClusterGroupRef = useRef(null);
  // const mapRef = useRef(null);

  const [zoomLevel, setZoomLevel] = useState(13);
  const { state } = useContext(AuthContext);

  const createCustomClusterIcon = (cluster) => {
    const map = ref.current
    let zoom = map.getZoom();
    const count = cluster.getChildCount();
    let clusterSizeClass = 'marker-cluster marker-cluster-small';
    if (zoom >= 16 && zoom < 17) {
      clusterSizeClass = 'marker-cluster marker-cluster-medium';
    } else if (zoom >= 17) {
      clusterSizeClass = 'marker-cluster marker-cluster-large';
    }

    return L.divIcon({
      html: `<span>${count}</span>`,
      className: clusterSizeClass, // Add a custom CSS class for styling
      iconSize: [0, 0],
      iconAnchor: [8, 9], // Adjust anchor to center the icon
    });
  };

  useEffect(() => {
    if (ref.current) {
      props.setReportedMarkerIcon?.(createReportedMarkerIcon(ref.current.getZoom()));
      props.setReportedEvacMarkerIcon?.(createReportedEvacMarkerIcon(ref.current.getZoom()));
      props.setReportedSIPMarkerIcon?.(createReportedSIPMarkerIcon(ref.current.getZoom()));
      props.setSIPMarkerIcon?.(createSIPMarkerIcon(ref.current.getZoom()));
      props.setUTLMarkerIcon?.(createUTLMarkerIcon(ref.current.getZoom()));
      props.setReturnRequestedMarkerIcon?.(createReturnRequestedMarkerIcon(ref.current.getZoom()));
      props.setClosedMarkerIcon?.(createClosedMarkerIcon(ref.current.getZoom()));
      props.setOperationsMarkerIcon?.(createOperationsMarkerIcon(ref.current.getZoom()));
      props.setCheckMarkerIcon?.(createCheckMarkerIcon(ref.current.getZoom()));
      props.setGrayCheckMarkerIcon?.(createGrayCheckMarkerIcon(ref.current.getZoom()));
      props.setStarMarkerIcon?.(createStarMarkerIcon(ref.current.getZoom()));
      props.setPinMarkerIcon?.(createPinMarkerIcon(ref.current.getZoom()));
    }
  }, [ref.current && ref.current.getZoom()]);

  return (
    <>
    <LeafletMap ref={ref} className={props.className || "d-block"} center={props.center} zoom={zoomLevel} bounds={props.bounds} boundsOptions={props.boundsOptions} zoomControl={props.zoomControl === false ? false : true} onMoveEnd={props.onMoveEnd}>
      {/* <Legend position={props.legend_position || "bottomleft"} metric={false} /> */}
      <ScaleControl position="bottomleft" metric={false} />
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
      />
      <ZoomHandler setZoomLevel={setZoomLevel} />
      <FirePerimetersLayer {...props.firePerimetersProps} />
      <EvacZonesLayer {...props.evacZonesProps} />
      {state?.incident?.id && (
        <CaltopoFeaturesLayer id={state.incident.id} />
      )}
      <MapEventsHandler onMoveEnd={props.onMoveEnd} />
      {props.manual_spider !== true ? <MarkerClusterGroup iconCreateFunction={(cluster) => createCustomClusterIcon(cluster)} maxClusterRadius={0} spiderfyDistanceMultiplier={0.9} spiderfyOnMaxZoom={true} zoomToBoundsOnClick={false} animate={true} chunkedLoading>
      {props.children}
      </MarkerClusterGroup> : props.children}
    </LeafletMap>
    </>
  );
});

export const AddressLookup = ({setData, initialBounds, handleClose, ...props}) => {

  const childRef = useRef(null);
  const [incidentLatLon, setIncidentLatLon] = useState({lat:0, lng:0});

  const clearAddress = () => {
    childRef.current.value = "";
    setData(prevState => ({ ...prevState, bounds:initialBounds }));
    if (handleClose) {
      handleClose();
    }
  };

  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    if (props.state?.incident?.coordinates) {
      setIncidentLatLon({lat:Number(props.state.incident.coordinates[0]), lng:Number(props.state.incident.coordinates[1])})
    }
  }, [props.state.incident]);

  const updateAddr = suggestion => {

    if (suggestion && suggestion.address_components) {
      // Extract location information from the return. Use short_name for the state.
      let components={};
      suggestion.address_components.forEach(function(k,v1) {k.types.forEach(function(v2, k2){v2 !== "administrative_area_level_1" ? components[v2]=k.long_name : components[v2]=k.short_name});});

      // Build formatted street number + name string.
      let address = "";
      if (components.street_number) {
        address = components.street_number + " " + components.route;
      }
      else if (components.route) {
        address = components.route;
      }
      else {
        address = components.intersection;
      }

      setData(prevState => ({ ...prevState, bounds:[[suggestion.geometry.location.lat(), suggestion.geometry.location.lng()]] }));
    }
  }

  return (
    <>
      <Row className="me-0 d-flex" style={{maxHeight:"37px"}}>
        <Col className="flex-grow-1 pe-0">
          <Autocomplete
            {...props}
            onPlaceSelected={(place) => {
              updateAddr(place);
              handleClose()
            }}
            onFocus={(event) => { event.target.setAttribute('autocomplete', 'off'); }}
            id="search"
            name="search"
            options={{
              bounds:{north:incidentLatLon.lat+.1, south:incidentLatLon.lat-.1, east:incidentLatLon.lng+.1, west:incidentLatLon.lng-.1},
              types: ["geocode"],
              componentRestrictions: { country: ["us", "ca"] },
            }}
            ref={childRef}
            key={String(incidentLatLon.lat)}
            apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
          />
        </Col>
      </Row>
      <Row>
        <Col>
        <Button variant="outline-light" className="float-end w-100 mt-2" style={{maxHeight:"37px"}} onClick={clearAddress} disabled={props.disabled ? true : false}>Reset</Button>
        </Col>
      </Row>
    </>
  );
}

export default Map;
